import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  alpha,
  useTheme,
  Chip,
  Fade,
  styled,
  FormHelperText
} from '@mui/material';
import {
  Info as InfoIcon,
  CurrencyExchange,
  MilitaryTech,
  StackedLineChart,
  LocalAtm,
  AccountBalanceWallet,
  Percent,
  Description,
  Brush,
  ColorLens,
  ShieldOutlined,
  Lock,
  Add,
  Help
} from '@mui/icons-material';
import { TwitterPicker } from 'react-color';

// Styled components for modern UI
const GradientBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(theme.palette.secondary.main, 0.07)} 100%)`,
  borderRadius: 16,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
}));

const TokenSymbolPreview = styled(Box)(({ theme, color }) => ({
  width: 64,
  height: 64,
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
  fontWeight: 'bold',
  color: theme.palette.getContrastText(color || theme.palette.primary.main),
  backgroundColor: color || theme.palette.primary.main,
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden'
}));

const InfoCard = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.info.main, 0.1),
  borderRadius: 8,
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'flex-start'
}));

const ColorIndicator = styled(Box)(({ color, theme }) => ({
  width: 24,
  height: 24,
  borderRadius: 6,
  backgroundColor: color || theme.palette.primary.main,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  cursor: 'pointer',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.1)'
  }
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: 8,
  fontWeight: 600,
  margin: theme.spacing(0.5),
  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.08)'
}));

// Color palette for the token
const tokenColorOptions = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow/Gold
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#F97316', // Orange
  '#6366F1', // Indigo
  '#84CC16', // Lime
];

const TokenSettingsModal = ({ open, onClose, token, onSave, isCreating = false }) => {
  const theme = useTheme();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    description: '',
    decimals: 2,
    initialSupply: 100000,
    maxSupply: 1000000,
    color: '#3B82F6',
    isTransferable: true,
    isMintable: true,
    isBurnable: true
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Initialize form with token data if editing
  useEffect(() => {
    if (token) {
      const initialData = {
        name: token.name || '',
        symbol: token.symbol || '',
        description: token.description || '',
        decimals: token.decimals !== undefined ? token.decimals : 2,
        initialSupply: token.initialSupply || token.totalSupply || 100000,
        maxSupply: token.maxSupply || token.totalSupply * 10 || 1000000,
        color: token.color || '#3B82F6',
        isTransferable: token.isTransferable !== undefined ? token.isTransferable : true,
        isMintable: token.isMintable !== undefined ? token.isMintable : true,
        isBurnable: token.isBurnable !== undefined ? token.isBurnable : true
      };
      
      setFormData(initialData);
    } else {
      // Reset form for new token creation
      setFormData({
        name: '',
        symbol: '',
        description: '',
        decimals: 2,
        initialSupply: 100000,
        maxSupply: 1000000,
        color: '#3B82F6',
        isTransferable: true,
        isMintable: true,
        isBurnable: true
      });
    }
  }, [token, open]);
  
  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Token name is required';
    }
    
    if (!formData.symbol.trim()) {
      errors.symbol = 'Token symbol is required';
    } else if (formData.symbol.length > 5) {
      errors.symbol = 'Symbol should be 5 characters or less';
    }
    
    if (formData.decimals < 0 || formData.decimals > 18) {
      errors.decimals = 'Decimals should be between 0 and 18';
    }
    
    if (formData.initialSupply <= 0) {
      errors.initialSupply = 'Initial supply must be greater than 0';
    }
    
    if (formData.maxSupply < formData.initialSupply) {
      errors.maxSupply = 'Max supply cannot be less than initial supply';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Map form data to the API expected format
      const submissionData = {
        // Required fields for the API
        name: formData.name,
        symbol: formData.symbol,
        description: formData.description || `${formData.name} token for community rewards`,
        
        // Convert economic values from the form to match API expectations
        baseRate: 1.0,
        totalSupply: parseInt(formData.initialSupply, 10),
        maxSupply: parseInt(formData.maxSupply, 10),
        
        // Map token features
        isTransferable: formData.isTransferable,
        isExchangeable: formData.isMintable, // Map our UI's "Mintable" to API's "isExchangeable"
        isActive: true,
        
        // Extra metadata that might be useful later
        color: formData.color,
        decimals: parseInt(formData.decimals, 10),
        
        // Initialize with empty arrays if creating new
        earningActivities: token?.earningActivities || [],
        usageOptions: token?.usageOptions || [],
        settings: token?.settings || {}
      };
      
      // Add _id if editing an existing token
      if (token && token._id) {
        submissionData._id = token._id;
      }
      
      console.log('Submitting token data:', submissionData);
      
      // Pass the mapped data to the onSave callback
      onSave(submissionData);
    } catch (error) {
      console.error('Error preparing token data:', error);
      alert('Error preparing token data. Please check console for details.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  // Handle color selection
  const handleColorChange = (color) => {
    setFormData(prev => ({
      ...prev,
      color: color.hex
    }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: 'white',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <AccountBalanceWallet sx={{ mr: 1 }} />
        {isCreating ? 'Create New Token' : 'Edit Token'}
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Token Identity */}
          <Grid item xs={12}>
            <GradientBox>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <CurrencyExchange sx={{ mr: 1 }} />
                Token Identity
              </Typography>
              
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        name="name"
                        label="Token Name"
                        value={formData.name}
                        onChange={handleChange}
                        fullWidth
                        error={!!formErrors.name}
                        helperText={formErrors.name}
                        placeholder="e.g. MemberCoin"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocalAtm />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="symbol"
                        label="Token Symbol"
                        value={formData.symbol}
                        onChange={handleChange}
                        fullWidth
                        error={!!formErrors.symbol}
                        helperText={formErrors.symbol || "3-5 letters (e.g. MBC, COIN)"}
                        inputProps={{ maxLength: 5 }}
                        placeholder="e.g. MBC"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MilitaryTech />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Typography variant="body2" sx={{ mr: 2 }}>Token Color:</Typography>
                        <Box sx={{ position: 'relative' }}>
                          <ColorIndicator 
                            color={formData.color} 
                            onClick={() => setShowColorPicker(!showColorPicker)}
                          />
                          {showColorPicker && (
                            <Box sx={{ 
                              position: 'absolute', 
                              zIndex: 2,
                              mt: 1
                            }}>
                              <Box 
                                sx={{ 
                                  position: 'fixed', 
                                  top: 0, 
                                  right: 0, 
                                  bottom: 0, 
                                  left: 0,
                                  cursor: 'default'
                                }} 
                                onClick={() => setShowColorPicker(false)} 
                              />
                              <TwitterPicker 
                                color={formData.color}
                                onChange={handleColorChange}
                                colors={tokenColorOptions}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
                
                <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Preview</Typography>
                    <TokenSymbolPreview color={formData.color}>
                      {formData.symbol || '?'}
                    </TokenSymbolPreview>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    label="Token Description"
                    value={formData.description}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Describe your token's purpose and use cases..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                          <Description />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </GradientBox>
          </Grid>
          
          {/* Token Economics */}
          <Grid item xs={12}>
            <GradientBox>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <StackedLineChart sx={{ mr: 1 }} />
                Token Economics
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="initialSupply"
                    label="Initial Supply"
                    value={formData.initialSupply}
                    onChange={handleChange}
                    fullWidth
                    type="number"
                    error={!!formErrors.initialSupply}
                    helperText={formErrors.initialSupply}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalAtm />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="maxSupply"
                    label="Maximum Supply"
                    value={formData.maxSupply}
                    onChange={handleChange}
                    fullWidth
                    type="number"
                    error={!!formErrors.maxSupply}
                    helperText={formErrors.maxSupply}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalAtm />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="decimals"
                    label="Decimal Places"
                    value={formData.decimals}
                    onChange={handleChange}
                    fullWidth
                    type="number"
                    error={!!formErrors.decimals}
                    helperText={formErrors.decimals || "Usually 2-18 (e.g. 2 for $0.01)"}
                    InputProps={{
                      inputProps: { min: 0, max: 18 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Percent />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <InfoCard>
                  <InfoIcon color="info" sx={{ mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2">
                      The <strong>initial supply</strong> is the amount of tokens created at launch. 
                      The <strong>maximum supply</strong> is the total amount that can ever exist.
                      Only change the <strong>decimal places</strong> if you have specific requirements.
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <StyledChip 
                        label={`${formData.initialSupply.toLocaleString()} initial tokens`} 
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <StyledChip 
                        label={`${(formData.maxSupply - formData.initialSupply).toLocaleString()} mintable in future`} 
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </InfoCard>
              </Box>
            </GradientBox>
          </Grid>
          
          {/* Token Features */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1
            }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <ShieldOutlined sx={{ mr: 1 }} />
                Token Features
              </Typography>
              <Button 
                startIcon={showAdvanced ? null : <Add />} 
                onClick={() => setShowAdvanced(!showAdvanced)}
                size="small"
              >
                {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
              </Button>
            </Box>
            
            <GradientBox>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isTransferable}
                        onChange={handleChange}
                        name="isTransferable"
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography>Member Transfers</Typography>
                        <Tooltip title="When enabled, members can transfer tokens to each other">
                          <IconButton size="small">
                            <Help fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isMintable}
                        onChange={handleChange}
                        name="isMintable"
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography>Mintable</Typography>
                        <Tooltip title="When enabled, admins can create new tokens (up to max supply)">
                          <IconButton size="small">
                            <Help fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isBurnable}
                        onChange={handleChange}
                        name="isBurnable"
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography>Burnable</Typography>
                        <Tooltip title="When enabled, tokens can be permanently removed from circulation">
                          <IconButton size="small">
                            <Help fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>
              
              <Fade in={showAdvanced}>
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Advanced Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Additional token features will be available in future updates, including:
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap' }}>
                    <StyledChip 
                      label="Time-based vesting" 
                      size="small" 
                      disabled
                      variant="outlined"
                      icon={<Lock fontSize="small" />}
                    />
                    <StyledChip 
                      label="External exchange" 
                      size="small" 
                      disabled
                      variant="outlined"
                      icon={<Lock fontSize="small" />}
                    />
                    <StyledChip 
                      label="Auto-distribution" 
                      size="small" 
                      disabled
                      variant="outlined"
                      icon={<Lock fontSize="small" />}
                    />
                  </Box>
                </Box>
              </Fade>
            </GradientBox>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 4,
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)'
            }
          }}
        >
          {loading ? 'Processing...' : isCreating ? 'Create Token' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TokenSettingsModal;